/**
 * Luggero Lux first-visit popup.
 *
 * The markup is only rendered when Liquid has already decided the visitor is
 * eligible, so this handles the two things Liquid cannot know: whether they
 * have seen it before, and whether they arrived on a campaign that is already
 * converting them.
 *
 * Nothing here opens the popup early. The delay is the point: at three
 * seconds the visitor has not seen a product, so there is nothing to
 * interrupt them from, and a discount that arrives before the product reads
 * as desperation on a premium range.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-lux-popup]');
  if (!root) return;

  var COOKIE = 'eo_lux_popup';
  var FREQ = root.getAttribute('data-frequency') || 'first_time';
  var DISMISS_DAYS = parseInt(root.getAttribute('data-dismiss-days'), 10);
  var COMPLETE_DAYS = parseInt(root.getAttribute('data-complete-days'), 10);
  if (isNaN(DISMISS_DAYS)) DISMISS_DAYS = 30;
  if (isNaN(COMPLETE_DAYS)) COMPLETE_DAYS = 3650;
  var panel = root.querySelector('.lux-pop__panel');
  var screens = {};
  [].forEach.call(root.querySelectorAll('[data-lux-screen]'), function (el) {
    screens[el.getAttribute('data-lux-screen')] = el;
  });

  // the prototype's own maps, transcribed. The two [CONFIRM] lines that used
  // to sit here reached the live homepage - they were in this file's
  // fallbacks, in the snippet's | default: filters and in the schema, three
  // layers deep, and clearing a setting could not have stopped them. Removed
  // 2026-10-15. Nothing is invented to fill them: no weight, no line.
  /*
    The names and the reasons come from theme settings, through data attributes
    on the root, so the words are editable without touching this file. The ids
    below - short, week, long, set, survive, light, looks, value - stay here
    because they are structure: the tag map is keyed on them and the Worker
    validates against the same enum. Only the text moves.

    The fallbacks are the shipped copy. An attribute that arrives empty leaves
    the screen reading sensibly rather than blank.
  */
  function attr(name, fallback) {
    var v = root.getAttribute(name);
    return v === null || v === '' ? fallback : v;
  }

  var REC = {
    short: attr('data-rec-short', 'Cabin'),
    week: attr('data-rec-week', 'Medium'),
    long: attr('data-rec-long', 'Large'),
    set: attr('data-rec-set', 'three-piece set')
  };
  var REC_SENTENCE = attr('data-rec-sentence', 'Start with the [rec].');
  var REASON = {
    survive: attr('data-reason-survive',
      'Built for the belt, not the showroom.'),
    light: attr('data-reason-light', ''),
    looks: attr('data-reason-looks',
      'A finish chosen to still look right in three years, not three trips.'),
    value: attr('data-reason-value',
      'The smallest step up in price for the biggest step up in build.')
  };
  // the ids the prototype uses, mapped to the customer tags the spec asks for
  var TAG = {
    q1: { short: 'trip_cabin', week: 'trip_medium', long: 'trip_large', set: 'trip_set' },
    q2: { self: 'for_self', gift: 'for_gift' },
    q3: { survive: 'wants_durability', light: 'wants_light', looks: 'wants_looks', value: 'wants_value' }
  };

  var answers = {};
  var opened = false;
  var lastFocus = null;

  function cookieGet(name) {
    try {
      var all = document.cookie ? document.cookie.split('; ') : [];
      for (var i = 0; i < all.length; i++) {
        var eq = all[i].indexOf('=');
        if (all[i].slice(0, eq) === name) return decodeURIComponent(all[i].slice(eq + 1));
      }
    } catch (e) {}
    return null;
  }

  function cookieSet(name, value, days) {
    try {
      var d = new Date();
      d.setTime(d.getTime() + days * 864e5);
      document.cookie = name + '=' + encodeURIComponent(value) +
        ';expires=' + d.toUTCString() + ';path=/;SameSite=Lax';
    } catch (e) {}
  }

  /*
    Through EngineEvents, which queues until gtag exists.

    The old fallback pushed to dataLayer when gtag was missing, which looks
    like a safety net and is not one: this store runs gtag.js directly, and
    gtag.js does not read events out of dataLayer. Anything pushed there while
    the Google channel was still loading was discarded - and this popup fires
    its first event on page one of a visit, which is exactly when gtag is
    least likely to have arrived.
  */
  function ga(name, params) {
    try {
      if (window.EngineEvents) {
        window.EngineEvents.send(name, params || {});
      } else if (typeof window.gtag === 'function') {
        window.gtag('event', name, params || {});
      }
    } catch (e) {}
  }

  /* ---------------------------------------------------------- eligibility */

  /*
    Staff opt-out: visit any page with ?noluxpopup=1 once per device.

    Deliberately not an IP allow-list. The office and the plant are on dynamic
    addresses, so an IP list starts working, stops working the day the address
    changes, and gives no sign that it has - staff would quietly re-enter the
    funnel and the numbers would drift without anyone knowing why. A cookie is
    per device rather than per network, so it also covers a phone on mobile
    data and someone working from home.
  */
  try {
    if (new URLSearchParams(window.location.search).get('noluxpopup') === '1') {
      cookieSet(COOKIE, 'optout', 730);
      return;
    }
  } catch (e) {}

  // already seen, dismissed, completed or opted out
  if (cookieGet(COOKIE)) return;

  // arrived on a campaign that is already converting them: do not interrupt it
  try {
    var camp = new URLSearchParams(window.location.search).get('utm_campaign');
    if (camp && camp.toLowerCase() === 'diwali2026') return;
  } catch (e) {}

  /* --------------------------------------------------------------- screens */

  function show(name) {
    Object.keys(screens).forEach(function (k) {
      screens[k].hidden = (k !== name);
    });
    /*
      Focus the dialog, never the heading.

      This used to set tabindex="-1" on the screen's h2 and focus it, which is
      where the black ring came from - the browser's default indicator on the
      element receiving programmatic focus, appearing on open and clearing on
      the first click. The dialog already carries tabindex="-1" and now has
      outline:none, so the ring is gone while the trap, Escape and the
      announcement all still work.

      Focus moves on every screen change, not only on open: the control just
      clicked is hidden by the time the next screen renders, and focus left on
      a hidden element falls to the body and takes the tab trap with it.
    */
    if (panel && panel.focus) {
      try { panel.focus({ preventScroll: true }); } catch (e) { panel.focus(); }
    }
  }

  function focusable() {
    return [].slice.call(panel.querySelectorAll(
      'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])'
    )).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function onKey(e) {
    if (e.key === 'Escape') { close('escape'); return; }
    if (e.key !== 'Tab') return;
    var list = focusable();
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function open() {
    if (opened || cookieGet(COOKIE)) return;
    opened = true;
    lastFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('data-open', '');
    // the scrollbar is left alone: hiding it shifts the page behind the panel,
    // and the brief is explicit that nothing may move
    show('1');
    document.addEventListener('keydown', onKey, true);
    ga('lux_popup_view', {});
    /*
      First visit only means exactly that: the cookie is written as it
      opens, so somebody who reads it and wanders off has still had their
      one look. Writing it on close would show it again to every visitor
      who never closed it.
    */
    if (FREQ === 'first_time') { cookieSet(COOKIE, 'seen', 3650); }
  }

  function close(how) {
    if (!opened) return;
    opened = false;
    root.removeAttribute('data-open');
    root.hidden = true;
    document.removeEventListener('keydown', onKey, true);
    if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
    if (how !== 'complete') {
      /*
        A dismissal under "until it is completed" writes nothing, so the
        popup returns next visit. Under the other two it holds for the
        window the setting gives it.
      */
      if (FREQ !== 'every_visit') { cookieSet(COOKIE, 'dismissed', DISMISS_DAYS); }
      ga('lux_popup_dismiss', { how: how || 'close' });
    } else {
      cookieSet(COOKIE, 'completed', COMPLETE_DAYS);
    }
  }

  /* --------------------------------------------------------------- trigger */

  var isMobile = window.matchMedia('(max-width: 749px)').matches;
  var delay = parseInt(root.getAttribute(isMobile ? 'data-delay-mobile' : 'data-delay-desktop'), 10) || (isMobile ? 25 : 15);
  var depth = parseInt(root.getAttribute(isMobile ? 'data-scroll-mobile' : 'data-scroll-desktop'), 10) || (isMobile ? 60 : 50);

  var timer = setTimeout(open, delay * 1000);

  function onScroll() {
    var h = document.documentElement.scrollHeight - window.innerHeight;
    if (h <= 0) return;
    var pct = (window.scrollY / h) * 100;
    if (pct >= depth) { cleanupTriggers(); open(); }
  }

  function onExit(e) {
    if (e.clientY <= 0) { cleanupTriggers(); open(); }
  }

  function cleanupTriggers() {
    clearTimeout(timer);
    window.removeEventListener('scroll', onScroll);
    document.removeEventListener('mouseout', onExit);
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  // exit intent is desktop only: there is no cursor to leave the viewport on a phone
  if (!isMobile) document.addEventListener('mouseout', onExit);

  /* ------------------------------------------------------------- behaviour */

  root.addEventListener('click', function (e) {
    var t = e.target.closest ? e.target.closest('[data-lux-close],[data-lux-scrim],[data-lux-start],[data-lux-skip],[data-lux-back],[data-lux-answer],[data-lux-shop]') : null;
    if (!t) return;

    if (t.hasAttribute('data-lux-close') || t.hasAttribute('data-lux-scrim')) {
      cleanupTriggers(); close('close'); return;
    }
    if (t.hasAttribute('data-lux-skip')) {
      cleanupTriggers(); cookieSet(COOKIE, 'skipped', 30);
      ga('lux_popup_dismiss', { how: 'just_show_me' });
      return;                                  // the link navigates on its own
    }
    if (t.hasAttribute('data-lux-shop')) { close('complete'); return; }
    if (t.hasAttribute('data-lux-start')) {
      cleanupTriggers(); ga('lux_popup_start', {}); show('q1'); return;
    }
    if (t.hasAttribute('data-lux-back')) { show(t.getAttribute('data-lux-back')); return; }

    var q = t.getAttribute('data-lux-answer');
    var v = t.getAttribute('data-value');
    answers[q] = v;
    ga('lux_popup_' + q, { answer: v });

    if (q === 'q1') { show('q2'); return; }
    if (q === 'q2') {
      if (v === 'corporate') {                 // leaves the consumer path here
        ga('lux_popup_corporate', { step: 'divert' });
        show('corp');
      } else {
        show('q3');
      }
      return;
    }
    if (q === 'q3') {
      var rec = REC[answers.q1] || REC.short;
      var tt = root.querySelector('[data-lux-rec-title]');
      var rr = root.querySelector('[data-lux-rec-reason]');
      var rw = root.querySelector('[data-lux-rec-word]');
      if (tt) tt.textContent = REC_SENTENCE.replace('[rec]', rec);
      /* Rule 1. .lux-pop__reason carries a top and bottom margin, so an
         empty reason left a gap where a sentence used to be. The line is
         empty whenever the setting is: 'lightest' has no copy until the
         plant sends a weight. */
      if (rr) {
        var rtext = REASON[v] || '';
        rr.textContent = rtext;
        rr.hidden = !rtext;
      }
      if (rw) rw.textContent = rec;
      show('rec');
    }
  });

  /* ----------------------------------------------------------------- send */

  /* The honeypot's value. A person leaves it empty; a script fills it in. */
  function hp(form) {
    var f = form.querySelector('[name="website"]');
    return f ? f.value : '';
  }

  function post(kind, payload, form, done) {
    var endpoint = root.getAttribute('data-endpoint');
    var err = form.querySelector('[data-lux-error]');
    var btn = form.querySelector('[data-lux-submit]');

    function fail(msg) {
      if (err) { err.textContent = msg; err.hidden = false; }
      if (btn) { btn.disabled = false; btn.textContent = btn.getAttribute('data-label') || btn.textContent; }
    }

    // a form that silently does nothing is worse than one that says so
    if (!endpoint) {
      fail('This form is not connected yet. Please call us on the number below.');
      return;
    }
    if (btn) { btn.setAttribute('data-label', btn.textContent); btn.disabled = true; btn.textContent = 'Sending…'; }
    if (err) err.hidden = true;

    fetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (r) {
      return r.ok ? r.json().catch(function () { return {}; })
                  : Promise.reject(new Error('HTTP ' + r.status));
    }).then(done).catch(function () {
      fail('That did not send. Please try again, or call us on the number below.');
    });
  }

  var consumerForm = root.querySelector('[data-lux-form="consumer"]');
  if (consumerForm) {
    consumerForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = consumerForm.querySelector('[name="email"]');
      if (!email || !email.value || email.value.indexOf('@') < 0) {
        var er = consumerForm.querySelector('[data-lux-error]');
        if (er) { er.textContent = 'Please enter an email address we can send the code to.'; er.hidden = false; }
        return;
      }
      var tags = ['lux_popup'];
      ['q1', 'q2', 'q3'].forEach(function (k) {
        var t = TAG[k] && TAG[k][answers[k]];
        if (t) tags.push(t);
      });
      post('consumer', {
        kind: 'consumer',
        email: email.value.trim(),
        phone: (consumerForm.querySelector('[name="phone"]') || {}).value || '',
        consent: !!(consumerForm.querySelector('[name="consent"]') || {}).checked,
        answers: answers,
        tags: tags,
        website: hp(consumerForm),
        recommendation: REC[answers.q1] || REC.short
      }, consumerForm, function (data) {
        /*
          No code in the reply is a real answer, not a failure: the daily cap
          has been reached, or the discount API refused. The Worker sends the
          words to show. Printing a made-up "LUX10" here instead would hand the
          customer a code that does not exist, which is worse than saying so.
        */
        var code = data && data.code;
        var title = root.querySelector('[data-lux-done-title]');
        var body = root.querySelector('[data-lux-done-body]');
        var back = root.querySelector('[data-lux-done-fallback]');
        var valid = root.querySelector('[data-lux-valid]');

        /*
          The end date comes from the price rule, never from copy written here.
          A shared price rule has one ends_at for every code on it, so there is
          no per-code expiry to print - and if the date moves in admin this
          sentence moves with it rather than quietly going stale.

          Read off the leading YYYY-MM-DD rather than through Date(), which
          would shift the day for anyone in another timezone and show a
          customer a date the rule does not hold.
        */
        if (valid) {
          var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
          var iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(String((data && data.valid_until) || ''));
          if (code && iso) {
            valid.textContent = 'Valid until ' + Number(iso[3]) + ' '
              + MONTHS[Number(iso[2]) - 1] + ' ' + iso[1] + '.';
            valid.hidden = false;
          } else {
            valid.hidden = true;
          }
        }
        /*
          With the offer off there is no code to be missing, so the "call us"
          line must not appear. The root carries the switch, so the script does
          not have to infer it from an absent field.
        */
        var offerOn = root.getAttribute('data-offer') !== 'false';

        /*
          Three cases, not two, which is the bug this replaces: a code; no code
          because the offer is off; and no code when one was expected. The old
          shape ran the code branch for the middle case and built the button as
          /discount/null - a dead link handed to every customer.
        */
        if (code) {
          var el = root.querySelector('[data-lux-code]');
          if (el) el.textContent = code;
          /*
            Shopify applies a discount to the session the moment a visitor lands
            on /discount/<code>, so the button carries it instead of asking
            anyone to remember it. The code stays on screen for whoever does not
            click through. The destination is read off the button's own href
            rather than written here, so this still follows routes.collections_url
            and works for a brand whose collection is not called lux.
          */
          var shop = root.querySelector('[data-lux-shop]');
          if (shop && (shop.getAttribute('href') || '').indexOf('/discount/') !== 0) {
            var dest = shop.getAttribute('href') || '/collections/lux';
            shop.setAttribute('href', '/discount/' + encodeURIComponent(code)
              + '?redirect=' + encodeURIComponent(dest));
          }
        } else if (offerOn) {
          if (title) title.textContent = 'We have your details.';
          if (body) body.hidden = true;
          if (back) {
            back.textContent = (data && data.message)
              || 'We could not issue your code automatically. Call us and we will apply it.';
            back.hidden = false;
          }
        }
        ga('lux_popup_submit', {
          recommendation: REC[answers.q1] || REC.short,
          code_issued: !!code,
          offer: offerOn
        });
        cookieSet(COOKIE, 'completed', 3650);
        show('done');
      });
    });
  }

  var corpForm = root.querySelector('[data-lux-form="corporate"]');
  if (corpForm) {
    corpForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var need = ['name', 'company', 'phone', 'quantity', 'email'];
      for (var i = 0; i < need.length; i++) {
        var f = corpForm.querySelector('[name="' + need[i] + '"]');
        if (!f || !f.value.trim()) {
          var er = corpForm.querySelector('[data-lux-error]');
          if (er) { er.textContent = 'Please fill in every field so we can call you properly.'; er.hidden = false; }
          f && f.focus();
          return;
        }
      }
      post('corporate', {
        kind: 'corporate',
        name: corpForm.querySelector('[name="name"]').value.trim(),
        company: corpForm.querySelector('[name="company"]').value.trim(),
        phone: corpForm.querySelector('[name="phone"]').value.trim(),
        quantity: corpForm.querySelector('[name="quantity"]').value.trim(),
        email: ((corpForm.querySelector('[name="email"]') || {}).value || '').trim(),
        answers: answers,
        website: hp(corpForm),
        tags: ['lux_popup', 'lux_popup_corporate']
      }, corpForm, function () {
        ga('lux_popup_corporate', { step: 'submitted' });
        /* the corporate branch posts into the same gifting enquiry flow as
           the two gifting pages, so it is one funnel and one enquiry_type.
           form_location is what tells the three apart. */
        ga('generate_lead', {
          form_location: 'lux_popup_corporate',
          enquiry_type: 'gifting'
        });
        cookieSet(COOKIE, 'completed', 3650);
        show('corpdone');
      });
    });
  }
})();
